import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useFormik } from 'formik';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import * as carApi from '../../lib/api/car';
import CarReservForm from '../../components/carmng/CarReservForm';
import { ko } from 'date-fns/locale';

const CarReservContainer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  const savedUser = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
  const editData = location.state?.data;

  const [carAssets, setCarAssets] = useState([]);
  const [visitList, setVisitList] = useState([]);
  const [isListOpen, setIsListOpen] = useState(false);
  const [isPartnerSelected, setIsPartnerSelected] = useState(false);
  const [partnerDisplayName, setPartnerDisplayName] = useState('');
  const [isTimeOverlapping, setIsTimeOverlapping] = useState(false);

  const initialValues = useMemo(() => ({
    logId: editData?.logId || '',
    usrNm: savedUser.usrNm || '',
    usrUuid: savedUser.usrUuid || '',
    dptNm: savedUser.dptNm || '',
    dptUuid: savedUser.dptUuid || '',
    carNumber: editData?.carNumber || '',
    carUuid: editData?.carUuid || '',
    startDate: editData?.startDate ? new Date(editData.startDate) : new Date(),
    endDate: editData?.endDate ? new Date(editData.endDate) : new Date(Date.now() + 60 * 60 * 1000),
    visitPartner: editData?.visitPartner || '',
    visitPartnerNm: editData?.visitPartnerNm || '',
  }), [editData, savedUser]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!values.carUuid) {
        alert("예약 차량을 선택해 주세요.");
        return;
      }

      if (!isPartnerSelected) {
        alert("돋보기를 눌러 거래처를 선택해 주세요.");
        return;
      }

      const now = new Date();
      const startDate = new Date(values.startDate);
      if (startDate < now) {
        enqueueSnackbar('현재 시각보다 이전 시간은 예약할 수 없습니다.', { variant: 'error' });
        return;
      }

      if (isTimeOverlapping) {
        enqueueSnackbar('선택하신 일정이 다른 예약과 겹칩니다.', { variant: 'warning' });
        return;
      }

      const payload = {
        ...values,
        startDate: format(new Date(values.startDate), 'yyyy-MM-dd HH:mm:ss'), 
        endDate: format(new Date(values.endDate), 'yyyy-MM-dd HH:mm:ss'),
      };

      try {
        await carApi.reservationUsageLog(payload);
        if (isMounted.current) {
          enqueueSnackbar('예약이 완료되었습니다.', { variant: 'success' });
          navigate('/carusage');
        }
      } catch (err) {
        if (isMounted.current) {
          enqueueSnackbar(err.response?.data?.message || err.message, { variant: 'error' });
        }
      }
    },
  });


  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const response = await carApi.getCarAssetList();
        if (isMounted.current) {
          const list = response.data.list || [];
          setCarAssets(list);
        }
      } catch (error) { 
        if (isMounted.current) console.error(error); 
      }
    };
    fetchAssets();
  }, [formik.values.carUuid]);

  useEffect(() => {
    if (!isPartnerSelected && formik.values.visitPartner && isMounted.current) {
      setPartnerDisplayName(formik.values.visitPartnerNm);
      setIsPartnerSelected(true);
    }
  }, [formik.values.visitPartner, isPartnerSelected]);

  return (
    <CarReservForm 
      formik={formik}
      carAssets={carAssets}
      visitList={visitList}
      setVisitList={setVisitList}
      isListOpen={isListOpen}
      setIsListOpen={setIsListOpen}
      isPartnerSelected={isPartnerSelected}
      setIsPartnerSelected={setIsPartnerSelected}
      partnerDisplayName={partnerDisplayName}
      setPartnerDisplayName={setPartnerDisplayName}
      isTimeOverlapping={isTimeOverlapping}
      setIsTimeOverlapping={setIsTimeOverlapping}
    />
  );
};

export default CarReservContainer;
